import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ChatFireService } from '../../gen2ai/chatfire.service';
import { Conversation, Message } from '../../models/chat.model';
import { CalendarNotesService, CalendarQuickNote } from '../../services/calendar-notes.service';

type ViewMode = 'month' | 'week';

interface DayCell {
  date: Date;
  label: number;
  inCurrentMonth: boolean;
  isToday: boolean;
  hasConversations: boolean;
  conversationCount: number;
  messageCount: number;
  hasNotes: boolean;
  notesCount: number;
}

@Component({
  selector: 'app-calendar-chat-history',
  templateUrl: './calendar-chat-history.component.html',
  styleUrls: ['./calendar-chat-history.component.scss']
})
export class CalendarChatHistoryComponent implements OnInit, OnDestroy {
  /**
   * Optional: if your Conversation model includes a userId field,
   * you can pass the current user's uid here to filter.
   * Otherwise, this component will simply show all conversations
   * supplied by ChatFireService.
   */
  @Input() uid?: string;

  viewMode: ViewMode = 'month';

  today = new Date();
  currentAnchorDate = this.stripTime(new Date());
  selectedDate = this.stripTime(new Date());

  days: DayCell[] = [];

  // Data from chat service
  private allConversations: Conversation[] = [];
  filteredConversations: Conversation[] = [];
  private resolvedUid: string | null = null;
  private readonly historyWindowDays = 60;

  // Daily aggregates: key = yyyy-MM-dd
  private dayAgg = new Map<
    string,
    {
      messageCount: number;
      conversationIds: Set<string>;
    }
  >();

  // Current selection
  selectedConversation: Conversation | null = null;
  selectedDayNotes: CalendarQuickNote[] = [];
  newNoteText = '';
  newNoteConversationId = '';
  copiedMessageId: string | null = null;

  private noteCountsByDate = new Map<string, number>();

  // Subscriptions
  private sub?: Subscription;

  constructor(
    private chatFire: ChatFireService,
    private calendarNotes: CalendarNotesService
  ) {}

  ngOnInit(): void {
    // Use UID-wide conversation history (same source family as dashboard sync),
    // then constrain view to the last two months.
    this.sub = combineLatest([this.chatFire.uid$, this.chatFire.allConversationsByUid$])
      .pipe(filter(([uid]) => !!uid))
      .subscribe(([uid, convs]) => {
        const activeUid = String(uid || '').trim();
        this.resolvedUid = activeUid && !activeUid.startsWith('anon-') ? activeUid : null;
        this.allConversations = this.chatFire.filterConversationsForPastDays(
          convs || [],
          this.historyWindowDays
        );
        this.applyUserFilter();
        this.recomputeDailyAggregation();
        this.noteCountsByDate = this.calendarNotes.getNotesCountMap();
        this.rebuildCalendar();
        this.ensureSelectedConversationValid();
        this.refreshNotesForSelectedDate();
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /* ------------------------------------------------------------------ */
  /*  View + navigation                                                  */
  /* ------------------------------------------------------------------ */

  setViewMode(mode: ViewMode): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.syncSelectedDateToView();
    this.rebuildCalendar();
    this.ensureSelectedConversationValid();
  }

  goPrev(): void {
    if (this.viewMode === 'month') {
      const d = new Date(this.currentAnchorDate);
      d.setMonth(d.getMonth() - 1);
      this.currentAnchorDate = this.stripTime(d);
    } else {
      const d = new Date(this.currentAnchorDate);
      d.setDate(d.getDate() - 7);
      this.currentAnchorDate = this.stripTime(d);
    }
    this.syncSelectedDateToView();
    this.rebuildCalendar();
    this.ensureSelectedConversationValid();
  }

  goNext(): void {
    if (this.viewMode === 'month') {
      const d = new Date(this.currentAnchorDate);
      d.setMonth(d.getMonth() + 1);
      this.currentAnchorDate = this.stripTime(d);
    } else {
      const d = new Date(this.currentAnchorDate);
      d.setDate(d.getDate() + 7);
      this.currentAnchorDate = this.stripTime(d);
    }
    this.syncSelectedDateToView();
    this.rebuildCalendar();
    this.ensureSelectedConversationValid();
  }

  goToday(): void {
    this.currentAnchorDate = this.stripTime(new Date());
    this.selectedDate = this.stripTime(new Date());
    this.rebuildCalendar();
    this.ensureSelectedConversationValid();
  }

  get headerLabel(): string {
    const d = this.currentAnchorDate;
    const month = d.toLocaleString(undefined, { month: 'long' });
    const year = d.getFullYear();

    if (this.viewMode === 'month') {
      return `${month} ${year}`;
    } else {
      const weekRange = this.getWeekRange(this.currentAnchorDate);
      const startLabel = weekRange.start.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      });
      const endLabel = weekRange.end.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year:
          weekRange.end.getFullYear() !== weekRange.start.getFullYear()
            ? 'numeric'
            : undefined
      });
      return `Week of ${startLabel} – ${endLabel}`;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Calendar building + aggregation                                   */
  /* ------------------------------------------------------------------ */

  private rebuildCalendar(): void {
    if (this.viewMode === 'month') {
      this.buildMonthView();
    } else {
      this.buildWeekView();
    }
  }

  private buildMonthView(): void {
    const anchor = this.currentAnchorDate;
    const year = anchor.getFullYear();
    const month = anchor.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const firstWeekday = firstOfMonth.getDay(); // 0 = Sun

    const startDate = new Date(year, month, 1 - firstWeekday);

    const cells: DayCell[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const inCurrentMonth = d.getMonth() === month;
      const isToday = this.isSameDay(d, this.today);
      const dateKey = this.toDateKey(d);
      const agg = this.dayAgg.get(dateKey);
      const conversationCount = agg ? agg.conversationIds.size : 0;
      const messageCount = agg ? agg.messageCount : 0;
      const notesCount = this.noteCountsByDate.get(dateKey) || 0;

      cells.push({
        date: d,
        label: d.getDate(),
        inCurrentMonth,
        isToday,
        hasConversations: conversationCount > 0,
        conversationCount,
        messageCount,
        hasNotes: notesCount > 0,
        notesCount
      });
    }

    this.days = cells;
  }

  private buildWeekView(): void {
    const weekRange = this.getWeekRange(this.currentAnchorDate);
    const cells: DayCell[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(weekRange.start);
      d.setDate(weekRange.start.getDate() + i);

      const isToday = this.isSameDay(d, this.today);
      const dateKey = this.toDateKey(d);
      const agg = this.dayAgg.get(dateKey);
      const conversationCount = agg ? agg.conversationIds.size : 0;
      const messageCount = agg ? agg.messageCount : 0;
      const notesCount = this.noteCountsByDate.get(dateKey) || 0;

      cells.push({
        date: d,
        label: d.getDate(),
        inCurrentMonth: true,
        isToday,
        hasConversations: conversationCount > 0,
        conversationCount,
        messageCount,
        hasNotes: notesCount > 0,
        notesCount
      });
    }

    this.days = cells;
  }

  private getWeekRange(anchor: Date): { start: Date; end: Date } {
    const d = this.stripTime(anchor);
    const dayOfWeek = d.getDay(); // 0 = Sun
    const start = new Date(d);
    start.setDate(d.getDate() - dayOfWeek);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return { start, end };
  }

  private applyUserFilter(): void {
    const activeUid = this.uid || this.resolvedUid;
    if (!activeUid) {
      this.filteredConversations = this.allConversations.slice();
      return;
    }
    // Conversation model may or may not have userId; treat it as optional.
    this.filteredConversations = this.allConversations.filter(
      (c: any) => c.uid === activeUid || c.userId === activeUid
    );
  }

  private recomputeDailyAggregation(): void {
    this.dayAgg.clear();
    for (const conv of this.filteredConversations) {
      const msgList = conv.messages || [];
      const datesSeenForConv = new Set<string>();

      for (const msg of msgList as Message[]) {
        if (!msg || !msg.timestamp) continue;
        const d =
          msg.timestamp instanceof Date
            ? msg.timestamp
            : new Date(msg.timestamp as any);
        const key = this.toDateKey(d);

        let agg = this.dayAgg.get(key);
        if (!agg) {
          agg = {
            messageCount: 0,
            conversationIds: new Set<string>()
          };
          this.dayAgg.set(key, agg);
        }

        agg.messageCount++;

        if (!datesSeenForConv.has(key)) {
          agg.conversationIds.add(conv.id);
          datesSeenForConv.add(key);
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Selection + derived lists                                         */
  /* ------------------------------------------------------------------ */

  selectDay(day: DayCell): void {
    this.selectedDate = this.stripTime(day.date);
    this.ensureSelectedConversationValid();
    this.refreshNotesForSelectedDate();
  }

  selectConversation(conv: Conversation): void {
    this.selectedConversation = conv;
  }

  conversationTopicLabel(conv: Conversation): string {
    const rawTitle = String(conv?.title || '').trim();
    if (rawTitle && rawTitle.toLowerCase() !== 'new conversation') {
      return rawTitle;
    }
    const longestUserWord = this.extractLongestUserWord(conv);
    return longestUserWord || 'Conversation';
  }

  get selectedDateLabel(): string {
    return this.selectedDate.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  get selectedDateSummary(): { conversationCount: number; messageCount: number } {
    const key = this.toDateKey(this.selectedDate);
    const agg = this.dayAgg.get(key);
    return {
      conversationCount: agg ? agg.conversationIds.size : 0,
      messageCount: agg ? agg.messageCount : 0
    };
  }

  get conversationsForSelectedDate(): Conversation[] {
    const key = this.toDateKey(this.selectedDate);
    const agg = this.dayAgg.get(key);
    if (!agg) return [];

    const ids = agg.conversationIds;
    return this.filteredConversations
      .filter((c) => ids.has(c.id))
      .sort((a, b) => {
        const aTime = this.resolveConversationTimestamp(a)?.getTime() || 0;
        const bTime = this.resolveConversationTimestamp(b)?.getTime() || 0;
        return bTime - aTime;
      });
  }

  formatConversationTimestamp(
    conv: Conversation,
    mode: 'local' | 'utc' = 'local'
  ): string {
    const ts = this.resolveConversationTimestamp(conv);
    if (!ts) {
      return mode === 'utc' ? 'UTC: --' : 'Local: --';
    }

    if (mode === 'utc') {
      const utcLabel = ts.toLocaleString(undefined, {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      return `UTC: ${utcLabel}`;
    }

    const localLabel = ts.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    return `Local: ${localLabel}`;
  }

  get messagesForSelectedConversation(): Message[] {
    if (!this.selectedConversation) return [];
    const msgs = this.selectedConversation.messages || [];
    return (msgs as Message[])
      .slice()
      .sort((a, b) => {
        const aTime = new Date(a.timestamp as any).getTime();
        const bTime = new Date(b.timestamp as any).getTime();
        return aTime - bTime;
      });
  }

  async copyConversationMessage(msg: Message): Promise<void> {
    const text = String(msg?.content || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      this.copiedMessageId = String(msg?.id || '');
      setTimeout(() => {
        if (this.copiedMessageId === String(msg?.id || '')) {
          this.copiedMessageId = null;
        }
      }, 1400);
    } catch {
      // no-op: clipboard can fail in restricted browser contexts
    }
  }

  addQuickNote(): void {
    const text = this.newNoteText.trim();
    if (!text) return;
    const conversation = this.conversationsForSelectedDate.find(
      conv => conv.id === this.newNoteConversationId
    );
    this.calendarNotes.addNote({
      dateKey: this.toDateKey(this.selectedDate),
      text,
      conversationId: conversation?.id,
      conversationTitle: conversation?.title || (conversation ? 'Conversation' : undefined)
    });
    this.newNoteText = '';
    this.newNoteConversationId = '';
    this.noteCountsByDate = this.calendarNotes.getNotesCountMap();
    this.rebuildCalendar();
    this.refreshNotesForSelectedDate();
  }

  deleteQuickNote(note: CalendarQuickNote): void {
    this.calendarNotes.deleteNote(note.id);
    this.noteCountsByDate = this.calendarNotes.getNotesCountMap();
    this.rebuildCalendar();
    this.refreshNotesForSelectedDate();
  }

  private ensureSelectedConversationValid(): void {
    const list = this.conversationsForSelectedDate;
    if (!list.length) {
      this.selectedConversation = null;
      return;
    }
    if (!this.selectedConversation || !list.some((c) => c.id === this.selectedConversation!.id)) {
      this.selectedConversation = list[0];
    }
  }

  private refreshNotesForSelectedDate(): void {
    this.selectedDayNotes = this.calendarNotes.getNotesForDate(this.toDateKey(this.selectedDate));
  }

  private syncSelectedDateToView(): void {
    const selected = this.stripTime(this.selectedDate);
    if (this.viewMode === 'month') {
      if (
        selected.getMonth() !== this.currentAnchorDate.getMonth() ||
        selected.getFullYear() !== this.currentAnchorDate.getFullYear()
      ) {
        this.selectedDate = this.stripTime(new Date(this.currentAnchorDate));
      }
      return;
    }

    const weekRange = this.getWeekRange(this.currentAnchorDate);
    if (selected < weekRange.start || selected > weekRange.end) {
      this.selectedDate = this.stripTime(weekRange.start);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private toDateKey(d: Date): string {
    return d.toISOString().slice(0, 10); // yyyy-MM-dd
  }

  private extractLongestUserWord(conv: Conversation): string {
    const userText = (conv?.messages || [])
      .filter((msg) => msg?.role === 'user')
      .map((msg) => String(msg?.content || ''))
      .join(' ');

    const words = userText.match(/\b[\p{L}\p{N}'-]+\b/gu) || [];
    let longest = '';
    for (const word of words) {
      const clean = word.trim();
      if (clean.length > longest.length) {
        longest = clean;
      }
    }
    return longest;
  }

  private resolveConversationTimestamp(conv: Conversation): Date | null {
    const updated = this.toValidDate((conv as any)?.updatedAt);
    if (updated) return updated;

    const created = this.toValidDate((conv as any)?.createdAt);
    if (created) return created;

    const latestMessage = (conv.messages || [])
      .map((msg) => this.toValidDate((msg as any)?.timestamp))
      .filter((value): value is Date => !!value)
      .sort((a, b) => b.getTime() - a.getTime())[0];

    return latestMessage || null;
  }

  private toValidDate(value: any): Date | null {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  trackByDayIndex(index: number, _day: DayCell): number {
    return index;
  }

  trackByConvId(_index: number, conv: Conversation): string {
    return conv.id;
  }

  trackByMsgId(_index: number, msg: Message): string {
    return msg.id;
  }
}
